import { useEffect, useState, useMemo } from 'react';
import { employeeApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Input from '../../components/ui/Input.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { cn, formatDays } from '@/lib/utils';

const STATUS_VARIANT = { pending: 'warning', approved: 'success', rejected: 'danger', cancelled: 'default' };

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

function formatDate(d) {
  if (!d) return '\u2014';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch { return d; }
}

export default function MyRequestsPage() {
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    async function load() {
      try {
        const res = await employeeApi.listRequests();
        setRequests(res.requests || []);
      } catch (err) {
        toast.error(`Failed to load requests: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    let list = requests;
    if (statusFilter !== 'all') list = list.filter((r) => r.status === statusFilter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((r) =>
        r.typeName?.toLowerCase().includes(s) ||
        r.typeId?.toLowerCase().includes(s) ||
        r.reason?.toLowerCase().includes(s)
      );
    }
    return list;
  }, [requests, statusFilter, search]);

  const columns = [
    {
      key: 'typeName',
      label: 'Type',
      render: (r) => (
        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ background: r.typeColor || '#64748b' }}
          />
          <span className="font-medium">{r.typeName || r.typeId}</span>
        </div>
      ),
    },
    {
      key: 'dates',
      label: 'Dates',
      // A single-day request reads as one date, not a range.
      render: (r) => (r.startDate === r.endDate
        ? formatDate(r.startDate)
        : `${formatDate(r.startDate)} \u2013 ${formatDate(r.endDate)}`),
    },
    {
      key: 'days',
      label: 'Days',
      render: (r) => `${formatDays(r.days)} day${r.days !== 1 ? 's' : ''}`,
    },
    {
      key: 'status',
      label: 'Status',
      render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'default'}>{r.status}</Badge>,
    },
    {
      key: 'submittedAt',
      label: 'Submitted',
      render: (r) => {
        if (!r.submittedAt) return '\u2014';
        return new Date(r.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      },
    },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight">My Requests</h1>
      <p className="mb-6 text-muted-foreground">
        {requests.length} total &middot; {requests.filter((r) => r.status === 'pending').length} pending
      </p>

      {/* Status filter tabs */}
      <div className="mb-4 flex flex-wrap gap-2">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={cn(
              'rounded-md border px-3.5 py-1.5 text-sm transition-colors',
              statusFilter === f.value
                ? 'border-primary bg-primary/10 font-semibold text-primary'
                : 'border-input text-muted-foreground hover:bg-muted'
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mb-4">
        <Input
          placeholder="Search requests..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-[400px]"
        />
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={loading}
        emptyMessage={requests.length === 0 ? 'No requests yet.' : 'No requests match your filters.'}
        onRowClick={(r) => { window.location.href = `/my-requests/${encodeURIComponent(r.rowKey)}`; }}
        keyFn={(r) => r.rowKey}
      />
    </div>
  );
}
